import { supabase } from '@/integrations/supabase/client';
import { ApiService } from '@/services/api/ApiService';
import {
  BulkUpdateRequest,
  BulkUpdateJob,
  BulkUpdateJobItemStatus,
  BulkUpdateJobItemSummary,
  BulkUpdateJobItemsResponse,
  BulkUpdateJobFilters,
  BulkUpdateJobTarget,
  BulkUpdateJobsResponse,
} from '@/types/agent/bulk-update';
import { getBulkJobLabel } from './bulkJobLabel';

// Everything but the settings snapshot, which is large and only read by the undo job.
const ITEM_COLUMNS = 'id, job_id, agent_id, status, error, updates, applied_hash, restored_at, claimed_at, failed_at, created_at';

export class BulkUpdateService {
  private static jobLabels = new Map<number, string>();

  static async previewSilenceTimeout(target: BulkUpdateJobTarget): Promise<number> {
    const data = await ApiService.callEdge("handle-agent", { action: "apply-silence-timeout", preview: true, ...target });
    if (!data?.success) {
      throw new Error(data?.message || data?.error || "Failed to count genies");
    }
    return data.genieCount;
  }

  static async applySilenceTimeout(target: BulkUpdateJobTarget): Promise<{ jobId: number; genieCount: number }> {
    const data = await ApiService.callEdge("handle-agent", { action: "apply-silence-timeout", ...target });
    if (!data?.success) {
      throw new Error(data?.message || data?.error || "Failed to apply the silence timeout");
    }
    return { jobId: data.jobId, genieCount: data.genieCount };
  }

  static async previewPhoneGoals(target: BulkUpdateJobTarget): Promise<number> {
    const data = await ApiService.callEdge("handle-agent", { action: "apply-phone-goals", preview: true, ...target });
    if (!data?.success) {
      throw new Error(data?.message || data?.error || "Failed to count genies");
    }
    return data.genieCount;
  }

  static async applyPhoneGoals(target: BulkUpdateJobTarget): Promise<{ jobId: number; genieCount: number }> {
    const data = await ApiService.callEdge("handle-agent", { action: "apply-phone-goals", ...target });
    if (!data?.success) {
      throw new Error(data?.message || data?.error || "Failed to turn on phone goals");
    }
    return { jobId: data.jobId, genieCount: data.genieCount };
  }

  static async previewBulkUpdateAll(target: BulkUpdateJobTarget): Promise<number> {
    const data = await ApiService.callEdge("handle-agent", { action: "bulk-update-all", preview: true, ...target });
    if (!data?.success) {
      throw new Error(data?.message || data?.error || "Failed to count genies");
    }
    return data.genieCount;
  }

  static async bulkUpdateAll(
    target: BulkUpdateJobTarget,
    params: Record<string, any>,
  ): Promise<{ jobId: number; genieCount: number }> {
    const data = await ApiService.callEdge("handle-agent", { action: "bulk-update-all", ...target, params });
    if (!data?.success) {
      throw new Error(data?.message || data?.error || "Failed to start the bulk update");
    }
    return { jobId: data.jobId, genieCount: data.genieCount };
  }

  static async repairDefaultPronunciations(jobId: number): Promise<{ jobId: number; genieCount: number }> {
    const data = await ApiService.callEdge("handle-agent", { action: "repair-default-pronunciations", jobId });
    if (!data?.success) {
      throw new Error(data?.message || data?.error || "Failed to start the repair");
    }
    return { jobId: data.jobId, genieCount: data.genieCount };
  }

  static async restoreJob(jobId: number, agentIds?: string[]): Promise<{ jobId: number; genieCount: number }> {
    const data = await ApiService.callEdge("handle-agent", { action: "restore-bulk-job", jobId, agentIds });
    if (!data?.success) {
      throw new Error(data?.message || data?.error || "Failed to start the undo");
    }
    return { jobId: data.jobId, genieCount: data.genieCount };
  }

  static async countRestorableItems(jobId: number): Promise<number> {
    const { count, error } = await supabase
      .from('agent_bulk_job_items')
      .select('id', { count: 'exact', head: true })
      .eq('job_id', jobId)
      .eq('status', 'completed')
      .not('applied_hash', 'is', null)
      .is('restored_at', null);

    if (error) throw error;
    return count || 0;
  }

  static async getJobs(
    filters?: BulkUpdateJobFilters,
    pageSize: number = 50,
    offset: number = 0
  ): Promise<BulkUpdateJobsResponse> {
    let query = supabase
      .from('agent_bulk_jobs')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false });

    if (filters?.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }

    const { data: jobs, count, error } = await query.range(offset, offset + pageSize - 1);

    if (error) throw error;
    if (!jobs || jobs.length === 0) return { data: [], count: count || 0 };

    const jobIds = jobs.map((job) => job.id);
    const { data: items, error: itemsError } = await supabase
      .from('agent_bulk_job_items')
      .select('job_id, status')
      .in('job_id', jobIds);

    if (itemsError) throw itemsError;

    const counts = new Map<number, { total: number; completed: number; failed: number; pending: number }>();
    for (const item of items || []) {
      if (item.job_id === null) continue;
      const entry = counts.get(item.job_id) || { total: 0, completed: 0, failed: 0, pending: 0 };
      entry.total += 1;
      if (item.status === 'completed') entry.completed += 1;
      else if (item.status === 'failed') entry.failed += 1;
      else entry.pending += 1;
      counts.set(item.job_id, entry);
    }

    // Every item in a job carries the same kind of update, so one is enough to label it.
    // A label never changes once a job has items, so realtime refetches reuse it.
    const labels = await Promise.all(
      jobs.map(async (job) => {
        const known = BulkUpdateService.jobLabels.get(job.id);
        if (known) return known;

        const { data: sample, error: sampleError } = await supabase
          .from('agent_bulk_job_items')
          .select('updates')
          .eq('job_id', job.id)
          .limit(1)
          .maybeSingle();

        if (sampleError) throw sampleError;

        const label = getBulkJobLabel(job.kind, sample?.updates ?? null);
        if (sample) BulkUpdateService.jobLabels.set(job.id, label);
        return label;
      }),
    );

    const data = jobs.map((job, index) => ({
      ...job,
      ...(counts.get(job.id) || { total: 0, completed: 0, failed: 0, pending: 0 }),
      label: labels[index],
    }));

    return { data, count: count || 0 };
  }

  static async bulkUpdate(request: BulkUpdateRequest): Promise<{ success: boolean; jobId: number }> {
    const { data, error } = await supabase.functions.invoke('handle-agent', {
      body: {
        action: 'bulk-update',
        agentIds: request.agentIds,
        params: request.params,
      },
    });

    if (error || data?.success === false) {
      throw new Error(error?.message || data?.message || 'Bulk update failed');
    }

    return data;
  }

  static async getJobStatus(jobId: number): Promise<BulkUpdateJob> {
    const { data, error } = await supabase
      .from('agent_bulk_jobs')
      .select('*')
      .eq('id', jobId)
      .single();

    if (error) throw error;
    return data;
  }

  static async getJobItems(jobId: number): Promise<BulkUpdateJobItemSummary[]> {
    const { data, error } = await supabase
      .from('agent_bulk_job_items')
      .select(ITEM_COLUMNS)
      .eq('job_id', jobId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  static async getJobItemsWithAgentNames(
    jobId: number,
    status: BulkUpdateJobItemStatus,
    pageSize: number = 50,
    offset: number = 0
  ): Promise<BulkUpdateJobItemsResponse> {
    const query = supabase
      .from('agent_bulk_job_items')
      .select(`${ITEM_COLUMNS}, agents(name)`, { count: 'exact' })
      .eq('job_id', jobId);

    // Matches getJobs, which counts every item that isn't completed or failed as pending.
    const filtered =
      status === 'pending'
        ? query.or('status.is.null,status.not.in.(completed,failed)')
        : query.eq('status', status);

    const { data, count, error } = await filtered
      .order('id', { ascending: true })
      .range(offset, offset + pageSize - 1);

    if (error) throw error;

    return {
      data: (data || []).map(({ agents, ...item }) => ({
        ...item,
        agent_name: agents?.name ?? null,
      })),
      count: count || 0,
    };
  }
}
